import type { FeedbackPayload } from "./types";

export async function sendFeedback(
  endpoint: string,
  apiKey: string,
  payload: FeedbackPayload,
): Promise<{ ok: true; id: string; view_token?: string; view_url?: string }> {
  const url = endpoint.replace(/\/$/, "") + "/api/feedback/ingest";
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-feedback-key": apiKey,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let message = "Failed to send feedback";
    try {
      const err = (await res.json()) as { message?: string; error?: string };
      message = err.message ?? err.error ?? message;
    } catch {
      // body wasn't JSON
    }
    throw new Error(message);
  }
  return res.json();
}

/**
 * Two-step screenshot upload:
 *   1. Ask our API for a signed Supabase Storage upload URL (for the right ext).
 *   2. PUT the image blob directly to that URL with the matching content-type.
 * Returns the storage path which the widget then includes in the ingest POST.
 *
 * The capture path produces image/png; user upload/paste produce image/jpeg
 * (canvas re-encode). We derive the storage extension + PUT content-type from
 * the blob so the bucket's mime check passes and the path carries the right suffix.
 */
export async function uploadScreenshot(
  endpoint: string,
  apiKey: string,
  image: Blob,
): Promise<string> {
  const isJpeg = image.type === "image/jpeg" || image.type === "image/jpg";
  const ext = isJpeg ? "jpg" : "png";
  const contentType = isJpeg ? "image/jpeg" : "image/png";

  const signUrl = endpoint.replace(/\/$/, "") + "/api/feedback/screenshot-url";
  const signRes = await fetch(signUrl, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-feedback-key": apiKey,
    },
    body: JSON.stringify({ ext }),
  });
  if (!signRes.ok) throw new Error("Couldn't get screenshot upload URL");
  const { url, path } = (await signRes.json()) as { url: string; path: string };

  const putRes = await fetch(url, {
    method: "PUT",
    headers: { "content-type": contentType },
    body: image,
  });
  if (!putRes.ok) throw new Error("Screenshot upload failed");

  return path;
}
